if (!customElements.get('kn-footer-clearance')) {
  customElements.define('kn-footer-clearance', class extends HTMLElement {
    connectedCallback() {
      this.bottom = this.closest('.kf').querySelector('.kf-bottom');
      this.listeners = new AbortController();
      this.roots = new Map();
      this.hosts = new Map();
      this.queue = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          if (this.isConnected) this.measure();
        });
      };
      const signal = this.listeners.signal;
      window.addEventListener('resize', () => { this.discover(); }, { passive: true, signal });
      window.addEventListener('load', () => { this.discover(); }, { once: true, signal });
      this.bodyObserver = new MutationObserver((records) => {
        const previousRoots = this.roots.size;
        let addedElement = false;
        records.forEach((record) => record.addedNodes.forEach((node) => {
          if (node instanceof Element && !this.closest('.kf').contains(node)) {
            addedElement = true;
            this.discoverTree(node);
          }
        }));
        if (addedElement) this.discover(false);
        if (this.roots.size !== previousRoots || Array.from(this.roots.values()).some(({ host }) => !host.isConnected)) this.queue();
      });
      this.bodyObserver.observe(document.body, { childList: true, subtree: true });
      this.discover();
    }

    discoverTree(node) {
      if (!(node instanceof Element)) return;
      const pending = [{ node, depth: 0 }];
      let inspected = 0;
      while (pending.length && inspected++ < 40) {
        const current = pending.shift();
        if (current.node.shadowRoot) this.watch(current.node.shadowRoot, current.node);
        if (current.depth < 2) {
          Array.from(current.node.children).forEach((child) => pending.push({ node: child, depth: current.depth + 1 }));
        }
      }
    }

    discover(notify = true) {
      Array.from(document.body.children).forEach((node) => this.discoverTree(node));
      document.querySelectorAll('#zsiq_float, [id*="brandagent" i], [id*="brand-agent" i], [id*="msba" i]').forEach((node) => {
        if (!this.hosts.has(node)) {
          const observer = new MutationObserver(() => { this.discoverTree(node); this.queue(); });
          observer.observe(node, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
          this.hosts.set(node, observer);
        }
        if (node.shadowRoot) this.watch(node.shadowRoot, node);
        else if (node.id === 'zsiq_float') this.watch(node, node);
      });
      if (notify) this.queue();
    }

    watch(root, host) {
      if (this.roots.has(root)) return;
      const observer = new MutationObserver(this.queue);
      observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
      if (root !== host) observer.observe(host, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
      root.addEventListener('transitionend', this.queue, { signal: this.listeners.signal });
      root.addEventListener('animationend', this.queue, { signal: this.listeners.signal });
      this.roots.set(root, { host, observer });
    }

    measure() {
      let top = innerHeight;
      for (const [root, entry] of this.roots) {
        if (!entry.host.isConnected) {
          entry.observer.disconnect();
          this.roots.delete(root);
          continue;
        }
        const nodes = [entry.host, ...root.querySelectorAll('*')];
        for (const node of nodes) {
          const style = getComputedStyle(node);
          if (style.position !== 'fixed' || style.display === 'none' || style.visibility !== 'visible' || style.pointerEvents === 'none') continue;
          const rect = node.getBoundingClientRect();
          // Reserve space for launchers/proactive hints, not an intentionally opened chat dialog.
          if (rect.width <= 20 || rect.width >= 430 || rect.height <= 20 || rect.height >= 220) continue;
          if (rect.right <= 0 || rect.left >= innerWidth || rect.top < innerHeight - 260 || rect.top >= innerHeight) continue;
          top = Math.min(top, rect.top);
        }
      }
      const clearance = top < innerHeight ? Math.ceil(innerHeight - top + 12) : 0;
      const minimum = innerWidth < 750 ? 64 : 88;
      const currentPadding = parseFloat(getComputedStyle(this.bottom).paddingBottom);
      const desiredPadding = Math.max(minimum, clearance);
      if (Math.abs(desiredPadding - currentPadding) < 0.5) return;

      // Do not shorten the document in a way that would clamp the visitor's scroll position.
      const delta = desiredPadding - currentPadding;
      if (delta < 0 && scrollY > Math.max(0, document.documentElement.scrollHeight + delta - innerHeight)) return;

      const footer = this.closest('.kf');
      const footerTop = footer.getBoundingClientRect().top;
      const contentTop = this.bottom.getBoundingClientRect().top;
      const previous = this.bottom.style.getPropertyValue('--kf-widget-clearance');
      this.bottom.style.setProperty('--kf-widget-clearance', `${clearance}px`);
      // A short page's flexible main row can redistribute space. Revert before paint if content moves.
      if (Math.abs(footer.getBoundingClientRect().top - footerTop) > 0.5 || Math.abs(this.bottom.getBoundingClientRect().top - contentTop) > 0.5) {
        if (previous) this.bottom.style.setProperty('--kf-widget-clearance', previous);
        else this.bottom.style.removeProperty('--kf-widget-clearance');
      }
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.bodyObserver?.disconnect();
      this.roots?.forEach(({ observer }) => observer.disconnect());
      this.hosts?.forEach((observer) => observer.disconnect());
      cancelAnimationFrame(this.frame);
      this.bottom?.style.removeProperty('--kf-widget-clearance');
    }
  });
}
